import { Door, Location } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useCallback, useMemo } from 'react';
import { createDeviceFilterChips } from 'v2/filters/devices/createDeviceFilterChips';
import { createDeviceFilterSections } from 'v2/filters/devices/createDeviceFilterSections';
import { filterBatteryStatusDefault, filterDeviceTypeDefault } from 'v2/filters/devices/deviceFilterConstants';
import {
  DevicesFilterState,
  FilterBatteryStatusState,
  FilterDeviceGroupsState,
  FilterDeviceTypeState,
  FilterOfficeModeState
} from 'v2/filters/devices/deviceFilterTypes';
import { filterDevices } from 'v2/filters/devices/filterDevices';
import { parseEnumParam, parseParamToBooleanRecord, serializeEnum } from 'v2/filters/filterUrlParams';
import { hasEnabledFilter } from 'v2/filters/hasEnabledFilter';
import { useFilterSearchParams } from 'v2/hooks/useFilterSearchParams/useFilterSearchParams';
import { useDeviceSearch } from 'v2/hooks/useSearch/useDeviceSearch';

interface Params {
  devices: Door[];
  lockGroups: Location[];
}

type DeviceFilterParam = 'search' | 'deviceGroups' | 'deviceType' | 'batteryStatus' | 'officeMode';

const DEVICE_FILTER_PARAM_KEYS: DeviceFilterParam[] = [
  'search',
  'deviceGroups',
  'deviceType',
  'batteryStatus',
  'officeMode'
];

const FILTER_DEVICE_GROUPS_VALUES = Object.values(FilterDeviceGroupsState);
const FILTER_OFFICE_MODE_VALUES = Object.values(FilterOfficeModeState);

export const useDevicesSearchAndFilters = ({ devices, lockGroups }: Params) => {
  const { translation } = useTranslation();

  const { searchParams, updateFilterParams, updateMultiSelectFilter } =
    useFilterSearchParams<DeviceFilterParam>(DEVICE_FILTER_PARAM_KEYS);

  const searchParam = searchParams.get('search');
  const deviceGroupsParam = searchParams.get('deviceGroups');
  const deviceTypeParam = searchParams.get('deviceType');
  const batteryStatusParam = searchParams.get('batteryStatus');
  const officeModeParam = searchParams.get('officeMode');

  const filterStr = searchParam ?? '';
  const filterDeviceGroups = useMemo(
    () => parseEnumParam(deviceGroupsParam, FILTER_DEVICE_GROUPS_VALUES),
    [deviceGroupsParam]
  );
  const filterDeviceType = useMemo(
    () => parseParamToBooleanRecord(deviceTypeParam, filterDeviceTypeDefault),
    [deviceTypeParam]
  );
  const filterBatteryStatus = useMemo(
    () => parseParamToBooleanRecord(batteryStatusParam, filterBatteryStatusDefault),
    [batteryStatusParam]
  );
  const filterOfficeMode = useMemo(() => parseEnumParam(officeModeParam, FILTER_OFFICE_MODE_VALUES), [officeModeParam]);

  const setFilterStr = useCallback(
    (value: string) => updateFilterParams(() => ({ search: value === '' ? undefined : value })),
    [updateFilterParams]
  );

  const setFilterDeviceGroups = useCallback(
    (value: FilterDeviceGroupsState) => updateFilterParams(() => ({ deviceGroups: serializeEnum(value) })),
    [updateFilterParams]
  );

  const setFilterDeviceType = useCallback(
    (value: FilterDeviceTypeState | ((current: FilterDeviceTypeState) => FilterDeviceTypeState)) =>
      updateMultiSelectFilter('deviceType', filterDeviceTypeDefault, value),
    [updateMultiSelectFilter]
  );

  const setFilterBatteryStatus = useCallback(
    (value: FilterBatteryStatusState | ((current: FilterBatteryStatusState) => FilterBatteryStatusState)) =>
      updateMultiSelectFilter('batteryStatus', filterBatteryStatusDefault, value),
    [updateMultiSelectFilter]
  );

  const setFilterOfficeMode = useCallback(
    (value: FilterOfficeModeState) => updateFilterParams(() => ({ officeMode: serializeEnum(value) })),
    [updateFilterParams]
  );

  const clearAllFilters = useCallback(() => {
    updateFilterParams(() => ({
      search: undefined,
      deviceGroups: undefined,
      deviceType: undefined,
      batteryStatus: undefined,
      officeMode: undefined
    }));
  }, [updateFilterParams]);

  const isAnyFilterEnabled = useMemo(
    () => hasEnabledFilter(filterStr, filterDeviceGroups, filterDeviceType, filterBatteryStatus, filterOfficeMode),
    [filterStr, filterDeviceGroups, filterDeviceType, filterBatteryStatus, filterOfficeMode]
  );

  const filters: DevicesFilterState = useMemo(
    () => ({
      filter: { filterDeviceGroups, filterDeviceType, filterBatteryStatus, filterOfficeMode },
      setters: { setFilterDeviceGroups, setFilterDeviceType, setFilterBatteryStatus, setFilterOfficeMode },
      clearAllFilters,
      isAnyFilterEnabled
    }),
    [
      filterDeviceGroups,
      filterDeviceType,
      filterBatteryStatus,
      filterOfficeMode,
      setFilterDeviceGroups,
      setFilterDeviceType,
      setFilterBatteryStatus,
      setFilterOfficeMode,
      clearAllFilters,
      isAnyFilterEnabled
    ]
  );

  const searchedDevices = useDeviceSearch({ devices, lockGroups, search: filterStr, translation });

  const filteredDevices = useMemo(
    () =>
      filterDevices({
        devices: searchedDevices,
        lockGroups,
        filterDeviceGroups,
        filterDeviceType,
        filterBatteryStatus,
        filterOfficeMode
      }),
    [searchedDevices, lockGroups, filterDeviceGroups, filterDeviceType, filterBatteryStatus, filterOfficeMode]
  );

  const countCaption = `${filteredDevices.length} ${translation.of} ${devices.length} ${translation.devices}`;

  const sections = useMemo(() => createDeviceFilterSections({ translation, filters }), [translation, filters]);
  const chips = useMemo(() => createDeviceFilterChips({ translation, filters }), [translation, filters]);

  return {
    filterStr,
    setFilterStr,
    filteredDevices,
    sections,
    chips,
    countCaption,
    isAnyFilterEnabled,
    clearAllFilters
  };
};
